import Logo from "@/components/Logo";
import { Link } from "react-router-dom";
import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowRight, Leaf, ShieldCheck, Truck, FileBarChart, Sparkles, Recycle,
  FlaskConical, HardHat, Handshake, ClipboardCheck, MapPin, Mail,
  CalendarClock, Camera,
} from "lucide-react";

export default function Landing() {
  const [contact, setContact] = useState({ name: "", company: "", email: "", interest: "Industrial waste programme", message: "", website: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submitContact = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await api.post("/contact", contact);
      setSent(true);
      setContact({ name: "", company: "", email: "", interest: "Industrial waste programme", message: "", website: "" });
      toast.success("Message sent. We'll be in touch within two working days.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not send your message. Please email info@hbtwaste.com.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ background: "var(--bone)" }}>
      <div className="grain" />

      <header className="glass-header">
        <div className="max-w-[1240px] mx-auto px-8 py-5 flex items-center justify-between">
          <Logo animate data-testid="landing-brand" />
          <div className="hidden md:flex items-center gap-7 text-sm">
            <a href="#services" className="hover:text-[var(--forest)]" data-testid="nav-services">Services</a>
            <a href="#approach" className="hover:text-[var(--forest)]">Approach</a>
            <a href="#digital" className="hover:text-[var(--forest)]" data-testid="nav-digital">Digital Advantage</a>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/login" className="btn-primary inline-flex items-center gap-2" data-testid="landing-signin-btn">
              Client Portal <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="max-w-[1240px] mx-auto px-8 pt-16 lg:pt-24 pb-20 relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <h1 className="font-display font-semibold tracking-tight text-5xl md:text-6xl lg:text-[76px] leading-[0.98]">
              Greener solutions.<br />
              <span style={{ color: "var(--terracotta)" }}>Smarter progress.</span>
            </h1>
            <p className="mt-8 max-w-xl text-base leading-relaxed text-neutral-600">
              HBT Waste Management is your end-to-end partner for industrial waste — from collection and transport to hazardous
              treatment, resource recovery and compliance consulting. We turn waste streams into measurable environmental impact.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-3">
              <a href="#services" className="btn-primary inline-flex items-center gap-2" data-testid="hero-primary-cta">
                Explore our services <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>

          <div className="lg:col-span-4 grid grid-cols-2 gap-3" data-testid="hero-stats">
            {[
              { label: "Waste streams", val: "4+" },
              { label: "Diversion target", val: "80%" },
              { label: "Industries served", val: "9" },
              { label: "Turnaround", val: "24h" },
            ].map((s, i) => (
              <div key={i} className="hbt-card p-5">
                <div className="hbt-label">{s.label}</div>
                <div className="font-display text-3xl font-semibold mt-3" style={{ color: "var(--forest)" }}>{s.val}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="divider" />

      {/* SERVICES */}
      <section id="services" className="max-w-[1240px] mx-auto px-8 py-20">
        <div className="hbt-label mb-3">What we do</div>
        <h2 className="font-display text-3xl md:text-4xl tracking-tight font-semibold max-w-2xl mb-14">
          Comprehensive waste management, engineered for industry.
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 stagger">
          {[
            { icon: HardHat, title: "Industrial Waste Management", body: "Tailored programmes for factories, mills and plants. We audit your streams, design a segregation plan, and run it end-to-end." },
            { icon: Truck, title: "Collection & Transportation", body: "Dedicated fleet, uniformed drivers and route optimisation. Scheduled or on-demand pickups with full manifest tracking." },
            { icon: FlaskConical, title: "Hazardous Waste Treatment", body: "Compliant handling of chemicals, medical, and industrial hazardous streams — from labelling through licensed treatment." },
            { icon: Recycle, title: "Resource Recovery & Recycling", body: "Recyclables sorted, baled and diverted from landfill. We maximise the value in every stream you produce." },
            { icon: Leaf, title: "Environmental Stewardship", body: "Waste-audits, reduction roadmaps and carbon-footprint measurement — helping you hit ESG targets with real data." },
            { icon: ClipboardCheck, title: "Consulting & Compliance", body: "Navigate environmental legislation with confidence. Permits, reporting and audit-ready documentation, done for you." },
          ].map((f, i) => (
            <div key={i} className="hbt-card p-7" data-testid={`service-card-${i}`}>
              <f.icon className="w-6 h-6 mb-6" strokeWidth={1.6} style={{ color: "var(--terracotta)" }} />
              <div className="font-display text-xl font-semibold mb-2">{f.title}</div>
              <div className="text-sm text-neutral-600 leading-relaxed">{f.body}</div>
            </div>
          ))}
        </div>
      </section>

      <div className="divider" />

      {/* MISSION / VISION */}
      <section id="approach" className="max-w-[1240px] mx-auto px-8 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          <div className="hbt-card p-10">
            <div className="hbt-label mb-3">Our mission</div>
            <div className="font-display text-2xl md:text-3xl font-semibold tracking-tight leading-tight">
              To deliver innovative, sustainable and reliable waste management solutions that protect the environment and support
              our clients’ operations.
            </div>
          </div>
          <div className="p-10 rounded-md relative overflow-hidden" style={{ background: "var(--forest)", color: "var(--bone)" }}>
            <div className="hbt-label mb-3" style={{ color: "rgba(245,242,234,0.7)" }}>Our vision</div>
            <div className="font-display text-2xl md:text-3xl font-semibold tracking-tight leading-tight">
              To be the preferred waste management partner, recognised for environmental stewardship, client satisfaction and
              industry-leading practices.
            </div>
          </div>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 stagger">
          {[
            { icon: Handshake, title: "Client-centric", body: "Solutions built around your operation — not a template." },
            { icon: ShieldCheck, title: "HSE-first", body: "Rigorous health, safety and environmental standards on every site." },
            { icon: Sparkles, title: "Innovation-led", body: "Digital tools and modern equipment that make waste measurable." },
          ].map((v, i) => (
            <div key={i} className="p-6">
              <v.icon className="w-6 h-6 mb-4" strokeWidth={1.6} style={{ color: "var(--forest)" }} />
              <div className="font-display text-lg font-semibold mb-1">{v.title}</div>
              <div className="text-sm text-neutral-600">{v.body}</div>
            </div>
          ))}
        </div>
      </section>

      <div className="divider" />

      {/* DIGITAL ADVANTAGE — the killer differentiator */}
      <section id="digital" className="max-w-[1240px] mx-auto px-8 py-20">
        <div className="hbt-label mb-3">The HBT digital advantage</div>
        <h2 className="font-display text-3xl md:text-5xl tracking-tight font-semibold max-w-3xl mb-6">
          Waste, measured. Sustainability, proven.
        </h2>
        <p className="text-neutral-600 max-w-2xl mb-14">
          Every client gets access to a private digital portal — a first in our sector. See exactly what we collect, where it
          goes, how much you divert from landfill, and download audit-ready reports the day the month closes.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 stagger">
          {[
            { icon: FileBarChart, title: "Interactive reports, any period", body: "Explore your waste, diversion and ESG performance on screen across any range — this quarter vs last, YTD, custom — with charts, breakdowns and an AI-written summary. Export to PDF, Excel or CSV when you need it." },
            { icon: Leaf, title: "Live ESG scorecard", body: "Diversion rate, CO₂ saved, recycling %, ESG score. Every kilogram we collect updates it in real time." },
            { icon: ShieldCheck, title: "Compliance certificates", body: "Signed proof of responsible disposal — one click. Perfect for audits, boards and buyer questionnaires." },
            { icon: CalendarClock, title: "Pickup scheduling", body: "Request collections from your desk. See status and ETA. No more email chains." },
            { icon: Camera, title: "Photo evidence", body: "Before/after photos for every collection, timestamped and geo-tagged. Full traceability." },
            { icon: Recycle, title: "Industry benchmarks", body: "See how your diversion rate compares to your sector’s average and top-quartile performers." },
          ].map((f, i) => (
            <div key={i} className="hbt-card p-7" data-testid={`digital-card-${i}`}>
              <f.icon className="w-6 h-6 mb-6" strokeWidth={1.6} style={{ color: "var(--forest)" }} />
              <div className="font-display text-lg font-semibold mb-2">{f.title}</div>
              <div className="text-sm text-neutral-600 leading-relaxed">{f.body}</div>
            </div>
          ))}
        </div>

        <div className="mt-12 flex flex-wrap items-center gap-3">
          <Link to="/login" className="btn-accent inline-flex items-center gap-2" data-testid="digital-cta">
            Explore the portal <ArrowRight className="w-4 h-4" />
          </Link>
          <a href="#contact" className="btn-outline">Talk to us about custom integrations</a>
        </div>
      </section>

      <div className="divider" />

      {/* HSE */}
      <section className="max-w-[1240px] mx-auto px-8 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <div className="hbt-label mb-3">Health, safety & environment</div>
            <h3 className="font-display text-3xl md:text-4xl font-semibold tracking-tight">
              Safety isn’t a protocol. It’s a principle.
            </h3>
          </div>
          <div className="lg:col-span-7 text-neutral-700 space-y-4 text-[15px] leading-relaxed">
            <p>Every HBT operation is designed around three non-negotiables: the well-being of our team, our client’s people, and the environment we all share.</p>
            <p>We adhere to all local and international environmental regulations, run regular HSE audits, and equip every driver and site technician with the training and PPE required to work safely at any client premises.</p>
            <p><b>Rigorous. Proactive. Documented.</b> That’s how we protect operations that trust us with their waste.</p>
          </div>
        </div>
      </section>

      <div className="divider" />

      {/* PROOF / STATS strip */}
      <section className="py-16" style={{ background: "var(--forest)", color: "var(--bone)" }}>
        <div className="max-w-[1240px] mx-auto px-8 grid grid-cols-2 md:grid-cols-4 gap-8">
          {[
            { v: "63%", l: "Avg. landfill diversion across our fleet" },
            { v: "12t+", l: "CO₂e avoided every month" },
            { v: "4", l: "Waste streams handled end-to-end" },
            { v: "24/7", l: "On-call response for critical sites" },
          ].map((s, i) => (
            <div key={i}>
              <div className="font-display text-4xl md:text-5xl font-semibold">{s.v}</div>
              <div className="text-xs uppercase tracking-[0.2em] opacity-70 mt-3">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="max-w-[1240px] mx-auto px-8 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          <div>
            <div className="hbt-label mb-3">Get in touch</div>
            <h3 className="font-display text-4xl md:text-5xl font-semibold tracking-tight">
              Let’s discuss your waste stream.
            </h3>
            <p className="mt-6 text-neutral-600 max-w-md">
              Send us a short brief — we’ll come back with an audit outline and a rough programme within two working days.
            </p>
            <div className="mt-10 space-y-4 text-sm">
              <div className="flex items-start gap-3"><MapPin className="w-4 h-4 mt-0.5 text-neutral-500" /><span>493 Matsapha Industrial Site</span></div>
              <div className="flex items-start gap-3"><Mail  className="w-4 h-4 mt-0.5 text-neutral-500" /><a href="mailto:info@hbtwaste.com" className="hover:underline">info@hbtwaste.com</a></div>
            </div>
          </div>

          <form
            className="hbt-card p-8"
            data-testid="contact-form"
            onSubmit={submitContact}
          >
            <div className="hbt-label mb-4">Send a message</div>
            {sent ? (
              <div className="space-y-3" data-testid="contact-success">
                <div className="font-display text-2xl font-semibold" style={{ color: "var(--forest)" }}>Thanks — we&apos;ve got it.</div>
                <p className="text-sm text-neutral-600">Your message is with our team. We&apos;ll come back within two working days.</p>
                <button type="button" onClick={() => setSent(false)} className="btn-outline mt-2" data-testid="contact-send-another">Send another message</button>
              </div>
            ) : (
            <div className="space-y-4">
              {/* honeypot — hidden from users, catches bots */}
              <input
                type="text"
                name="website"
                value={contact.website}
                onChange={(e) => setContact({ ...contact, website: e.target.value })}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                style={{ position: "absolute", left: "-10000px", width: 1, height: 1, opacity: 0 }}
              />
              <div>
                <label className="hbt-label block mb-2">Full name</label>
                <input name="name" required className="hbt-input" value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} data-testid="contact-name" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="hbt-label block mb-2">Company</label>
                  <input name="company" required className="hbt-input" value={contact.company} onChange={(e) => setContact({ ...contact, company: e.target.value })} data-testid="contact-company" />
                </div>
                <div>
                  <label className="hbt-label block mb-2">Email</label>
                  <input type="email" name="email" required className="hbt-input" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} data-testid="contact-email" />
                </div>
              </div>
              <div>
                <label className="hbt-label block mb-2">Interested in</label>
                <select name="interest" className="hbt-input" value={contact.interest} onChange={(e) => setContact({ ...contact, interest: e.target.value })} data-testid="contact-interest">
                  <option>Industrial waste programme</option>
                  <option>Recycling & resource recovery</option>
                  <option>Hazardous waste treatment</option>
                  <option>Environmental compliance / consulting</option>
                  <option>Custom digital integration</option>
                </select>
              </div>
              <div>
                <label className="hbt-label block mb-2">Message</label>
                <textarea name="message" rows={4} className="hbt-input" value={contact.message} onChange={(e) => setContact({ ...contact, message: e.target.value })} data-testid="contact-message" />
              </div>
              <button type="submit" disabled={sending} className="btn-primary w-full inline-flex items-center justify-center gap-2" data-testid="contact-submit">
                {sending ? "Sending…" : (<>Send message <ArrowRight className="w-4 h-4" /></>)}
              </button>
            </div>
            )}
          </form>
        </div>
      </section>

      <footer className="border-t" style={{ borderColor: "var(--border)" }}>
        <div className="max-w-[1240px] mx-auto px-8 py-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-xs text-neutral-500">
          <div>© {new Date().getFullYear()} HBT Waste Management · A subsidiary of HBT Group</div>
          <div className="flex items-center gap-6">
            <Link to="/login" className="hover:text-black">Client Portal</Link>
            <Link to="/login" className="hover:text-black">HBT Team Login</Link>
            <a href="#contact" className="hover:text-black">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
